import React, { useEffect, useState } from 'react';
import {
    getVideoStatsForMember,
    subscribeToVideoStats,
    DEBUG_STATS_POLL_INTERVAL_MS,
} from 'flow/utils/video-log-utils';
import { isVideoStatsDebugEnabled } from 'flow/utils/meeting-utils';
import LabelPlate from './LabelPlate';

type TileVideoStatsPlateProps = {
    memberName: string | null;
    isCompact?: boolean;
};

export type TileVideoStats = {
    frameRate?: number;
    bitrateKbps?: number;
    freezeCount?: number;
    resolution?: { width?: number; height?: number };
    lastPolledAt?: number;
};

/**
 * A read-out is treated as stale after two missed polls — one late tick is
 * normal timer jitter, two means this peer has stopped reporting.
 *
 * Derived from the debug rate specifically: this plate only renders in debug
 * sessions, and those are exactly the sessions the collector polls at
 * DEBUG_STATS_POLL_INTERVAL_MS (video-signaling-client passes the rate in).
 * If the poll rate ever becomes configurable beyond that split, this threshold
 * moves with the debug value, not with whatever a caller chose.
 */
export const STALE_STATS_AFTER_MS = DEBUG_STATS_POLL_INTERVAL_MS * 2;

export const isStale = (stats: TileVideoStats, now: number): boolean =>
    stats.lastPolledAt !== undefined &&
    now - stats.lastPolledAt > STALE_STATS_AFTER_MS;

/**
 * "17fps · 240kbps · 640×360" — resolution appended only once known and freezes
 * only once there are any, to keep the plate narrow on a healthy tile.
 *
 * Freezes are the running total for the tile rather than the last poll's delta,
 * so a brief freeze stays visible instead of flashing for one interval.
 */
export const formatVideoStats = (stats: TileVideoStats): string => {
    const parts = [
        `${Math.round(stats.frameRate ?? 0)}fps`,
        `${Math.round(stats.bitrateKbps ?? 0)}kbps`,
    ];
    const { width, height } = stats.resolution ?? {};
    if (width && height) {
        parts.push(`${width}×${height}`);
    }
    if (stats.freezeCount) {
        parts.push(`${stats.freezeCount} freeze`);
    }
    return parts.join(' · ');
};

/**
 * Debug-only read-out of what this tile is actually receiving (VID-396), for
 * the active-speaker load tests: FPS and bitrate expose both media-server
 * strain and how quickly a freshly subscribed tile ramps up after the speaker
 * changes.
 *
 * Renders nothing unless `?debug=true` or `?debug=local`, which is also what
 * registers peers for stats collection in the first place. The numbers are read
 * from that existing poll rather than measured here, so this adds no getStats()
 * calls and no per-frame work.
 */
const TileVideoStatsPlate: React.FC<TileVideoStatsPlateProps> = ({
    memberName,
    isCompact = false,
}) => {
    const [stats, setStats] = useState<TileVideoStats | null>(null);

    useEffect(() => {
        if (!isVideoStatsDebugEnabled() || !memberName) {
            // Clear, don't just stop reading: if memberName flips from a real
            // name to null while the plate stays mounted, the old subscription
            // is gone — so without this the previous member's numbers stay
            // painted forever, never even dimming (dimming needs a re-render).
            setStats(null);
            return undefined;
        }
        const read = () => setStats(getVideoStatsForMember(memberName));
        read();
        // Driven by the collector's poll rather than a second timer of our own:
        // an independent interval starts whenever the tile mounts, so it lands
        // at an arbitrary phase against the poll and adds up to a full interval
        // of staleness to numbers that are already one interval old.
        return subscribeToVideoStats(read);
    }, [memberName]);

    if (!stats) {
        return null;
    }

    return (
        <LabelPlate
            isCompact={isCompact}
            testId="meeting-tile-video-stats"
            className={isStale(stats, Date.now()) ? 'opacity-50' : undefined}
        >
            <span className="font-mono whitespace-nowrap">
                {formatVideoStats(stats)}
            </span>
        </LabelPlate>
    );
};

export default TileVideoStatsPlate;
